import { Observable } from 'rxjs';
import { filter } from 'rxjs/operators';
import { InfosDossiersPretsEtDonsHistoriqueModelDto } from 'src/graphql/generated-types';

export type CompareType<T> = (a: T, b: T) => -1 | 0 | 1;

export interface ColumnsParameters<T> {
    customComparator: (sort) => CompareType<T>;
    exporter: (loanRecord: T) => string;
}

export function arrayNotEmpty(array: any[]): boolean {
    return Array.isArray(array) && array.length > 0;
}

export function arrayEmpty(array: any[]): boolean {
    return !array || Array.isArray(array) && array.length === 0;
}

export function arrayOnlyOne(array: any[]): boolean {
    return Array.isArray(array) && array.length === 1;
}

export function arrayOfMoreThanOne(array: any[]): boolean {
    return Array.isArray(array) && array.length > 1;
}

export const filterNotEmptyArray = () => <T>(source: Observable<T[]>) => source.pipe(filter(arrayNotEmpty));

export function parseCSVToArray(csv: string, separator: string = '|'): string[][] {
    if (!csv) {
        return [];
    }
    return csv
        .split('\r\n') // split lines
        .map(l => l.replace(/\|$/g, '')) // remove empty cell ("|$") at end
        .map(l => l.split(separator)); // split cells
}

export function move(array: any[], moveIndex: number, toIndex: number) {
    const startElement = array.splice(moveIndex, 1);
    array.splice(toIndex, 0, ...startElement);
    return array;
}

export function getColumnsParameters<T>(sortFunction): ColumnsParameters<T> {
    return {
        customComparator: (sort) => compare((row) => sortFunction(row), sort.direction),
        exporter: (row) => sortFunction(row)
    };
}

export function compare<T>(sortParams: keyof T | ((params) => string) , direction: 'asc' | 'desc' = 'asc'): CompareType<T> {
    return (a, b) => {
        const v1 = typeof(sortParams) !== 'function' ? a[sortParams] : sortParams(a);
        const v2 = typeof(sortParams) !== 'function' ? b[sortParams] : sortParams(b);
        // if v1 === v2, comparator should return 0
        if (v1 === v2) {
            return 0;
        }
        // if asc comparator, coefficient = 1, -1 otherwise
        // used to revert comparaison when direction is 'desc'
        const coefficient: 1 | -1 = direction === 'asc' ? 1 : -1;
        return coefficient * (v1 > v2 ? 1 : -1) as 1 | -1;
    };
}

/**
 * remove duplicate in array
 */
export function uniq<T>(array: T[]): T[] {
    return [...new Set(array)];
}

/**
 * Divise le tableau en sous-tableaux. Utile pour du traitement par lots
 * @param array - tableau initial
 * @param size - taille maximum des sous-tableaux
 * @return T[][] - le tableau initial divisé en sous tableaux de `size` éléments maximum.
 */
export function chunk<T>(array: T[], size: number): T[][] {
    const result: T[][] = new Array(Math.ceil(array.length / size));

    for (let batchIndex = 0, arrayIndex = 0; batchIndex < array.length; batchIndex += size, arrayIndex++) {
        result[arrayIndex] = array.slice(batchIndex, batchIndex + size);
    }

    return result;
}
